import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useLocation } from 'react-router';

import { timeoutFetchSentryGuard } from 'Fetch';
import { getEligibilityCheck } from 'PrivateApis';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { type EligibilityStatus, type EnhancedEligibilityContextTypes, ELIGIBILITY_ERROR_REASON } from './enhancedEligibility.types';

const initialProps: EnhancedEligibilityContextTypes = {
  isEligible: undefined,
  isIneligibleWO: false,
  isLoading: false,
  reasonCode: undefined,
};

const EnhancedEligibilityContext = createContext(initialProps);

const EnhancedEligibilityProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { vehicleLookupActions: { update }, vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const { eligibilityStatus, enquiry, vrm } = vehicle ?? {};
  const { id: enquiryId } = enquiry ?? {};

  const { pathname } = useLocation();

  const checkEligibility = useCallback(async (checkVrm: string): Promise<EligibilityStatus> => {
    setIsLoading(true);

    const response: EligibilityStatus = await getEligibilityCheck(checkVrm)
      .catch((error) => {
        timeoutFetchSentryGuard(error, () => {
          window?.Sentry?.captureException(new Error(error), {
            extra: {
              vrm: checkVrm,
            },
            tags: {
              fetch: 'Eligibility status',
            },
          });
        });

        return { eligible: true };
      });

    update({
      vehicle: {
        eligibilityStatus: response,
      },
    });

    setIsLoading(false);

    return response;
  }, [update]);

  useEffect(() => {
    if (vrm && !pathname.includes(vrm)) {
      return;
    }

    if (vrm && enquiryId && !eligibilityStatus && !isLoading) {
      checkEligibility(vrm);
    }
  }, [
    checkEligibility,
    eligibilityStatus,
    enquiryId,
    isLoading,
    pathname,
    vrm,
  ]);

  const { eligible, reasonCode } = eligibilityStatus ?? {};

  const value = isLoading ? {
    ...initialProps,
    isLoading,
  } : {
    isEligible: eligible,
    isIneligibleWO: reasonCode === ELIGIBILITY_ERROR_REASON.UNSELLABLE_WO,
    isLoading,
    reasonCode,
  };

  return (
    <EnhancedEligibilityContext.Provider value={value}>
      {children}
    </EnhancedEligibilityContext.Provider>
  );
};

const useEnhancedEligibilityContext = () => useContext(EnhancedEligibilityContext);

export {
  EnhancedEligibilityProvider,
  EnhancedEligibilityContext,
  useEnhancedEligibilityContext,
};
